/**
 * Drone Requests rail surface (#374) — the requestor's discoverable "where are my drone requests"
 * home. Before this, the only place to track a raised mission was buried at the bottom of the Drone
 * Operator Coverage side-panel (DroneCoverageBody), reachable ONLY by first clicking a drone
 * coverage pin on the map — so after "Request live eyes" succeeded the requestor had nowhere to
 * watch the request progress. This lifts the SAME requestor missions list (DroneMissionsPanel —
 * status, dispatch terms ETA/price/feed, confirm/cancel) onto a persistent LeftRail tab, polled so
 * a freshly-raised request appears without a manual reload. It reports the open-request count up so
 * the tab can badge how many are still in flight.
 */
import { useEffect, useState } from 'react';
import type { DroneMissionView } from './api';
import { DroneMissionsPanel } from './DroneMissionsPanel';

/** Passive re-poll cadence (mirrors the AnomalyQueue 30 s tick) so a newly-raised request and any
 *  operator-driven status change surface here without the operator reopening the tab. */
export const REQUESTS_POLL_MS = 30_000;

export function DroneRequestsPanel({
  onSelectMission,
  onActiveCountChange,
}: {
  /** Open a mission's lifecycle + live-feed detail panel (row click) — reuses the COP's existing
   *  {kind:'mission'} SidePanel arm. */
  onSelectMission?: (m: DroneMissionView) => void;
  /** Report the count of OPEN (in-flight) requests up for the LeftRail tab badge. */
  onActiveCountChange?: (count: number) => void;
}): JSX.Element {
  // Each tick bumps the child's refreshKey → re-fetch. The panel stays mounted in the rail (the
  // LeftRail hides inactive tabs with CSS rather than unmounting), so this polls regardless of
  // which tab is active — the badge count stays live even while the operator is on another tab.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), REQUESTS_POLL_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="px-3 py-3" data-testid="drone-requests-panel">
      <p className="mb-1 text-[11px] leading-relaxed text-slate-400">
        Live status of the drone requests your agency raised from “Request live eyes”. Click a
        request to open its lifecycle and live feed. Updates automatically.
      </p>
      <DroneMissionsPanel
        refreshKey={tick}
        onSelectMission={onSelectMission}
        onActiveCountChange={onActiveCountChange}
      />
    </div>
  );
}
